import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Subject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { GqlQueryResult } from '.';
import { FlyFreelyConstants } from '../constants';
import {
    BatterySetDto,
    RpaDetailsDto,
    CreateBatterySetCommand,
    FlightHistorySummaryDto,
    ResultDto,
    TransferResourceCommand,
    UpdateBatterySetCommand,
    UpdateCustodianCommand,
    UpdateMaintenanceOrganisationCommand,
    UpdateNotesCommand
} from '../model/api';
import {
    ChangeEvent,
    entityChanged,
    entityDeleted,
    entityTransferred
} from './changes';
import { FlightHistoryEntry } from './flightHistory';
import { httpParamSerializer } from './service.helpers';

export interface BatterySetLastUse {
    rpa: {
        id: number;
        nickname: string;
        callSign: string;
        status: RpaDetailsDto.Status;
        isDummy: boolean;
    };
    lastUse: string;
}

@Injectable({
    providedIn: 'root'
})
export class BatterySetService {
    private baseUrl: string;
    private changeSource = new Subject<ChangeEvent<BatterySetDto>>();
    change$ = this.changeSource.asObservable();

    constructor(
        constants: FlyFreelyConstants,
        private http: HttpClient,
        private apollo: Apollo
    ) {
        this.baseUrl = constants.API_URL;
    }

    ngOnDestroy() {
        this.changeSource.complete();
    }

    private notifyBatterySetUpdated(updatedBatterySet: BatterySetDto) {
        this.changeSource.next(entityChanged(updatedBatterySet));
    }

    notifyBatterySetChanged() {
        this.changeSource.next(null);
    }

    findBatterySet(batterySetId: number, managingOrganisationId?: number) {
        return this.http.get<BatterySetDto>(
            `${this.baseUrl}/webapi/batterySets/${batterySetId}`,
            {
                params: httpParamSerializer({ managingOrganisationId })
            }
        );
    }

    createBatterySet(command: CreateBatterySetCommand) {
        return this.http
            .post<BatterySetDto>(`${this.baseUrl}/webapi/batterySets`, command)
            .toPromise()
            .then(batterySet => {
                this.notifyBatterySetUpdated(batterySet);
                return batterySet;
            });
    }

    updateBatterySet(batterySetId: number, command: UpdateBatterySetCommand) {
        return this.http
            .put<BatterySetDto>(
                `${this.baseUrl}/webapi/batterySets/${batterySetId}`,
                command
            )
            .pipe(tap(batterySet => this.notifyBatterySetUpdated(batterySet)));
    }

    findBatterySets(organisationId: number, at?: string, batteryId?: number) {
        return this.http.get<BatterySetDto[]>(
            `${this.baseUrl}/webapi/batterySets`,
            {
                params: httpParamSerializer({ organisationId, batteryId, at })
            }
        );
    }

    findFlightStatistics(batterySetId: number) {
        return this.http.get<FlightHistorySummaryDto>(
            `${this.baseUrl}/webapi/batterySets/${batterySetId}/history/summary`
        );
    }

    findBatterySetFlightHistory(
        criteria: {
            id: number;
            managingOrganisationId: number;
            includeType: ('LOGGED' | 'HISTORICAL' | 'UNASSIGNED')[];
        },
        order: 'ASC' | 'DESC',
        page = 0,
        pageSize = 10
    ) {
        return this.apollo
            .query<{
                findBatterySetFlightHistory: GqlQueryResult<FlightHistoryEntry>;
            }>({
                query: gql`
                    query find(
                        $criteria: ResourceFlightHistoryCriteria
                        $order: Direction
                        $pageSize: Int
                        $page: Int
                    ) {
                        findBatterySetFlightHistory(
                            criteria: $criteria
                            order: $order
                            page: $page
                            pageSize: $pageSize
                        ) {
                            count
                            results {
                                id
                                type
                                missionId
                                flightNumber
                                startTime
                                endTime
                                duration
                                visualLineOfSight
                                timeOfDay
                                rpaId
                                rpaType {
                                    make
                                    model
                                    rpaCategory
                                    performanceSpecifications {
                                        minTakeOffWeight
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: {
                    criteria,
                    order,
                    page,
                    pageSize
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => ({
                    ...r.data.findBatterySetFlightHistory,
                    pagination: { page, pageSize }
                }))
            );
    }
    findLastBatterySetUse(batterySetId: number, organisationId: number) {
        return this.apollo
            .query<{
                findLastBatterySetUse: BatterySetLastUse[];
            }>({
                query: gql`
                    query find($batterySetId: Long!, $organisationId: Long!) {
                        findLastBatterySetUse(
                            batterySetId: $batterySetId
                            organisationId: $organisationId
                        ) {
                            rpa {
                                id
                                nickname
                                callSign
                                status
                                isDummy
                            }
                            lastUse
                        }
                    }
                `,
                variables: {
                    batterySetId,
                    organisationId
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => r.data.findLastBatterySetUse)
            );
    }

    delete(batterySetId: number) {
        return this.http
            .delete<void>(`${this.baseUrl}/webapi/batterySets/${batterySetId}`)
            .pipe(
                tap(() => this.changeSource.next(entityDeleted(batterySetId)))
            );
    }

    onBatterySetChange() {
        this.changeSource.next(null);
    }

    updateBatterySetNotes(batterySetId: number, command: UpdateNotesCommand) {
        return this.http
            .put<BatterySetDto>(
                `${this.baseUrl}/webapi/batterySets/${batterySetId}/notes`,
                command
            )
            .pipe(tap(batterySet => this.notifyBatterySetUpdated(batterySet)));
    }

    updateCustodian(batterySetId: number, command: UpdateCustodianCommand) {
        return this.http
            .put<BatterySetDto>(
                `${this.baseUrl}/webapi/batterySets/${batterySetId}/custodian`,
                command
            )
            .pipe(tap(batterySet => this.notifyBatterySetUpdated(batterySet)));
    }

    updateMaintenanceOrganisation(
        batterySetId: number,
        command: UpdateMaintenanceOrganisationCommand
    ) {
        return this.http
            .put<BatterySetDto>(
                `${this.baseUrl}/webapi/batterySets/${batterySetId}/maintenanceOrganisation`,
                command
            )
            .pipe(tap(batterySet => this.notifyBatterySetUpdated(batterySet)));
    }

    transfer(batterySetId: number, command: TransferResourceCommand) {
        return this.http
            .post<ResultDto>(
                `${this.baseUrl}/webapi/batterySets/${batterySetId}/transfer`,
                command
            )
            .pipe(
                tap(() =>
                    this.changeSource.next(entityTransferred(batterySetId))
                )
            );
    }
}
